import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  useAddApvAction,
  useAnswerApv,
  useApv,
  useChemicals,
  useCompleteApv,
  useDeleteChemical,
  useSaveChemical,
  useStartApv,
  useUploadChemicalFile,
  useUsers
} from '../api/queries'
import { downloadFile } from '../api/client'
import type { ApvAnswerValue, ApvRound, Chemical, ChemicalSaveInput } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { formatDate } from '../lib/format'
import { useModuleEnabled } from '../navigation'
import { IncidentsTab } from './IntranetIncidents'
import { Card, EmptyState, ErrorBox, Loading } from './ui'

/**
 * Fanen "Arbejdsmiljø" (docs/DECISIONS.md beslutning 113 og 125): nærved-hændelser, APV og kemikalier - hver kan
 * være slået fra for sig. `?del=apv` eller `?del=chemicals` åbner den rigtige del (fra beskederne).
 */

type Part = 'incidents' | 'apv' | 'chemicals'
const errorText = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback)

export function WorkEnvironmentTab() {
  const moduleEnabled = useModuleEnabled()
  const [params] = useSearchParams()
  const parts: { key: Part; label: string; module: string }[] = [
    { key: 'incidents', label: '🦺 Nærved-hændelser', module: 'intranet_incidents' },
    { key: 'apv', label: '📋 APV', module: 'intranet_apv' },
    { key: 'chemicals', label: '🧪 Kemikalier', module: 'intranet_chemicals' }
  ].filter((p) => moduleEnabled(p.module)) as { key: Part; label: string; module: string }[]

  const [part, setPart] = useState<Part>(() => {
    const wanted = params.get('del')
    return (parts.find((p) => p.key === wanted) ?? parts[0])?.key ?? 'incidents'
  })
  const active = parts.some((p) => p.key === part) ? part : parts[0]?.key

  return (
    <div className="stack" style={{ gap: 16 }}>
      {parts.length > 1 && (
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
          {parts.map((p) => (
            <button key={p.key} className={`btn btn--sm ${active === p.key ? 'btn--primary' : ''}`} onClick={() => setPart(p.key)}>
              {p.label}
            </button>
          ))}
        </div>
      )}
      {active === 'incidents' && <IncidentsTab />}
      {active === 'apv' && <ApvSection />}
      {active === 'chemicals' && <ChemicalsSection />}
    </div>
  )
}

// --- APV ---

function ApvSection() {
  const { data, isLoading, error } = useApv()
  const { isIntranetAdmin } = useAuth()
  const [starting, setStarting] = useState(false)

  if (isLoading) return <Loading />
  if (error) return <ErrorBox error={error} />
  if (!data) return null

  const round = data.current
  const canStart = isIntranetAdmin && (!round || round.completedAt != null)

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="small muted">
        Arbejdspladsvurderingen (APV) skal laves mindst hvert 3. år. Alle svarer anonymt, og kontoret laver en
        handlingsplan ud fra svarene.
        {data.nextDue && <strong> Næste APV senest {formatDate(data.nextDue)}.</strong>}
      </div>

      {canStart && !starting && (
        <div><button className="btn btn--primary" onClick={() => setStarting(true)}>📋 Start ny APV</button></div>
      )}
      {starting && data.defaultQuestions && (
        <StartApvForm defaults={data.defaultQuestions} onClose={() => setStarting(false)} />
      )}

      {!round && !starting && <EmptyState>Der er ikke lavet en APV i systemet endnu.</EmptyState>}

      {round && round.isOpen && !round.iAnswered && <ApvQuestionnaire round={round} />}
      {round && round.isOpen && round.iAnswered && (
        <div className="map-notice">Tak! Dine svar til {round.title} er gemt - anonymt.</div>
      )}

      {round && isIntranetAdmin && <ApvResults round={round} />}
      {round && <ApvActionPlan round={round} isOffice={isIntranetAdmin} />}

      {data.history.length > 0 && (
        <Card title="Tidligere APV'er">
          <div className="stack" style={{ gap: 4 }}>
            {data.history.map((h) => (
              <div key={h.id} className="small">
                <strong>{h.title}</strong> · startet {formatDate(h.startedAt)}
                {h.completedAt ? ` · afsluttet ${formatDate(h.completedAt)}` : ' · ikke afsluttet'} · {h.responses} svar
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  )
}

function ApvQuestionnaire({ round }: { round: ApvRound }) {
  const answer = useAnswerApv()
  const [values, setValues] = useState<Record<number, ApvAnswerValue>>({})
  const [comments, setComments] = useState<Record<number, string>>({})
  const [error, setError] = useState<string | null>(null)
  const missing = round.questions.filter((q) => !values[q.id]).length

  async function send() {
    setError(null)
    try {
      await answer.mutateAsync({
        roundId: round.id,
        answers: round.questions.map((q) => ({ questionId: q.id, value: values[q.id], comment: comments[q.id]?.trim() || undefined }))
      })
    } catch (err) {
      setError(errorText(err, 'Svarene kunne ikke gemmes.'))
    }
  }

  let lastArea = ''
  return (
    <Card title={`📋 ${round.title} - svar senest ${formatDate(round.closesAt)}`}>
      <div className="stack" style={{ gap: 12 }}>
        <div className="small muted">
          Svarene er anonyme: der gemmes ikke, hvem der har svaret hvad - kun at du har svaret. Skriv gerne en kommentar
          ved "Problem", så kontoret ved, hvad der skal gøres.
        </div>
        {round.questions.map((q) => {
          const header = q.area !== lastArea ? q.area : null
          lastArea = q.area
          return (
            <div key={q.id} className="stack" style={{ gap: 6 }}>
              {header && <strong className="small" style={{ marginTop: 6 }}>{header}</strong>}
              <div className="small">{q.text}</div>
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {([['ok', '👍 OK'], ['problem', '⚠ Problem'], ['na', 'Ikke relevant']] as const).map(([value, label]) => (
                  <button
                    key={value}
                    type="button"
                    className={`btn btn--sm ${values[q.id] === value ? 'btn--primary' : ''}`}
                    onClick={() => setValues({ ...values, [q.id]: value })}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {values[q.id] === 'problem' && (
                <input
                  maxLength={500}
                  placeholder="Hvad er problemet? (valgfrit)"
                  value={comments[q.id] ?? ''}
                  onChange={(e) => setComments({ ...comments, [q.id]: e.target.value })}
                />
              )}
            </div>
          )
        })}
        {error && <div className="error-box">{error}</div>}
        <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn--primary" disabled={missing > 0 || answer.isPending} onClick={() => void send()}>
            {answer.isPending ? 'Sender ...' : 'Send mine svar (anonymt)'}
          </button>
          {missing > 0 && <span className="tiny muted">{missing} spørgsmål mangler svar</span>}
        </div>
      </div>
    </Card>
  )
}

function ApvResults({ round }: { round: ApvRound }) {
  const complete = useCompleteApv()
  const [error, setError] = useState<string | null>(null)

  return (
    <Card title={`Resultat - ${round.responses}${round.activeUsers ? ` af ${round.activeUsers}` : ''} har svaret`}>
      <div className="stack" style={{ gap: 10 }}>
        {round.questions.map((q) => {
          const r = q.result
          const total = r ? r.ok + r.problem + r.notRelevant : 0
          return (
            <div key={q.id} className="device-row stack" style={{ gap: 4 }}>
              <div className="small"><span className="muted">{q.area}:</span> {q.text}</div>
              {r && total > 0 ? (
                <>
                  <div style={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', background: '#E5E7EB' }}>
                    <span style={{ width: `${(r.ok / total) * 100}%`, background: '#16A34A' }} />
                    <span style={{ width: `${(r.problem / total) * 100}%`, background: '#DC2626' }} />
                  </div>
                  <div className="tiny muted">👍 {r.ok} · ⚠ {r.problem} · ikke relevant {r.notRelevant}</div>
                  {r.comments.map((c, i) => <div key={i} className="tiny">"{c}"</div>)}
                </>
              ) : (
                <div className="tiny muted">Ingen svar endnu</div>
              )}
            </div>
          )
        })}
        {round.completedAt ? (
          <div className="small muted">Afsluttet {formatDate(round.completedAt)}.</div>
        ) : (
          <div>
            <button
              className="btn btn--sm"
              disabled={complete.isPending}
              onClick={() => {
                if (!window.confirm('Afslut APV\'en? Der kan ikke svares mere, og den næste skal laves senest om 3 år.')) return
                setError(null)
                complete.mutate(round.id, { onError: (err) => setError(errorText(err, 'Kunne ikke afslutte.')) })
              }}
            >
              Afslut APV'en
            </button>
          </div>
        )}
        {error && <div className="error-box">{error}</div>}
      </div>
    </Card>
  )
}

function ApvActionPlan({ round, isOffice }: { round: ApvRound; isOffice: boolean }) {
  const add = useAddApvAction()
  const { data: users } = useUsers()
  const [adding, setAdding] = useState(false)
  const [questionId, setQuestionId] = useState('')
  const [title, setTitle] = useState('')
  const [assignee, setAssignee] = useState('')
  const [due, setDue] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function save() {
    setError(null)
    try {
      await add.mutateAsync({
        roundId: round.id,
        questionId: questionId ? Number(questionId) : null,
        title: title.trim(),
        assigneeUserId: Number(assignee),
        dueDate: due
      })
      setAdding(false)
      setTitle('')
      setQuestionId('')
    } catch (err) {
      setError(errorText(err, 'Punktet kunne ikke gemmes.'))
    }
  }

  return (
    <Card title="Handlingsplan">
      <div className="stack" style={{ gap: 8 }}>
        {round.actions.length === 0 && <div className="small muted">Ingen punkter endnu.</div>}
        {round.actions.map((a) => (
          <div key={a.id} className="device-row row row--between" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span className="small" style={{ textDecoration: a.isDone ? 'line-through' : undefined }}>
              {a.isDone ? '✅' : '⬜'} {a.title.replace(/^APV: /, '')}
            </span>
            <span className="tiny muted">
              {a.assignee?.displayName ?? 'Ingen ansvarlig'}
              {a.dueDate && ` · frist ${formatDate(a.dueDate)}`}
            </span>
          </div>
        ))}
        <div className="tiny muted">Punkterne er interne opgaver - de ansvarlige finder dem under fanen "Opgaver".</div>
        {isOffice && !adding && (
          <div><button className="btn btn--sm" onClick={() => setAdding(true)}>+ Tilføj punkt</button></div>
        )}
        {adding && (
          <div className="stack" style={{ gap: 8 }}>
            <select value={questionId} onChange={(e) => setQuestionId(e.target.value)}>
              <option value="">Fra spørgsmål (valgfrit) ...</option>
              {round.questions.map((q) => <option key={q.id} value={q.id}>{q.area}: {q.text}</option>)}
            </select>
            <input maxLength={200} placeholder="Hvad skal gøres? Fx Løftevogn til kabeltromler" value={title} onChange={(e) => setTitle(e.target.value)} />
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <select value={assignee} onChange={(e) => setAssignee(e.target.value)}>
                <option value="">Ansvarlig ...</option>
                {(users ?? []).filter((u) => u.isActive).map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}
              </select>
              <input type="date" value={due} onChange={(e) => setDue(e.target.value)} />
            </div>
            {error && <div className="error-box">{error}</div>}
            <div className="btn-group">
              <button className="btn btn--sm btn--primary" disabled={!title.trim() || !assignee || !due || add.isPending} onClick={() => void save()}>Gem punkt</button>
              <button className="btn btn--sm" onClick={() => setAdding(false)}>Annullér</button>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

function StartApvForm({ defaults, onClose }: { defaults: { area: string; text: string }[]; onClose: () => void }) {
  const start = useStartApv()
  const year = new Date().getFullYear()
  const [title, setTitle] = useState(`APV ${year}`)
  const [closes, setCloses] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() + 14)
    return d.toISOString().slice(0, 10)
  })
  const [text, setText] = useState(defaults.map((q) => `${q.area}: ${q.text}`).join('\n'))
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    setError(null)
    const questions = text
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const colon = line.indexOf(':')
        return colon > 0 && colon <= 60 ? { area: line.slice(0, colon).trim(), text: line.slice(colon + 1).trim() } : { area: 'Andet', text: line }
      })
    try {
      await start.mutateAsync({ title: title.trim(), closesAt: new Date(`${closes}T23:59:00`).toISOString(), questions })
      onClose()
    } catch (err) {
      setError(errorText(err, 'APV\'en kunne ikke startes.'))
    }
  }

  return (
    <Card title="📋 Start ny APV">
      <div className="stack" style={{ gap: 10 }}>
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Navn *</span>
            <input maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className="field">
            <span className="field__label">Svar senest *</span>
            <input type="date" value={closes} onChange={(e) => setCloses(e.target.value)} />
          </label>
        </div>
        <label className="field">
          <span className="field__label">Spørgsmål - ét pr. linje, "Område: spørgsmål"</span>
          <textarea rows={12} value={text} onChange={(e) => setText(e.target.value)} />
        </label>
        <div className="tiny muted">Alle får en besked. Spørgsmålene kopieres ind i runden, så de kan rettes til næste gang.</div>
        {error && <div className="error-box">{error}</div>}
        <div className="btn-group">
          <button className="btn btn--primary" disabled={!title.trim() || !closes || start.isPending} onClick={() => void submit()}>
            {start.isPending ? 'Starter ...' : 'Start APV\'en'}
          </button>
          <button className="btn" onClick={onClose}>Annullér</button>
        </div>
      </div>
    </Card>
  )
}

// --- Kemikalier ---

function ChemicalsSection() {
  const { isIntranetAdmin } = useAuth()
  const [showInactive, setShowInactive] = useState(false)
  const { data, isLoading, error } = useChemicals(showInactive)
  const [editing, setEditing] = useState<Chemical | null | undefined>(undefined)
  const [search, setSearch] = useState('')

  const query = search.trim().toLowerCase()
  const shown = (data ?? []).filter(
    (c) => !query || [c.name, c.supplier, c.usedFor, c.location].some((v) => v?.toLowerCase().includes(query))
  )

  return (
    <div className="stack" style={{ gap: 16 }}>
      <div className="small muted">
        Produkterne, vi bruger, med sikkerhedsdatablad og arbejdspladsbrugsanvisning. Læs brugsanvisningen, før du
        bruger et produkt første gang.
      </div>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <input style={{ maxWidth: 260 }} placeholder="Søg efter produkt ..." value={search} onChange={(e) => setSearch(e.target.value)} />
        {isIntranetAdmin && (
          <>
            <label className="row small" style={{ gap: 6 }}>
              <input type="checkbox" style={{ width: 'auto' }} checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
              Vis dem, vi ikke bruger mere
            </label>
            <button className="btn btn--primary btn--sm" onClick={() => setEditing(null)}>+ Tilføj produkt</button>
          </>
        )}
      </div>

      {editing !== undefined && <ChemicalForm chemical={editing} onClose={() => setEditing(undefined)} />}

      {isLoading && <Loading />}
      {error && <ErrorBox error={error} />}
      {data && shown.length === 0 && <EmptyState>Ingen produkter på listen.</EmptyState>}

      {shown.map((c) => (
        <Card key={c.id} title={`🧪 ${c.name}`}>
          <div className="stack" style={{ gap: 6, opacity: c.isActive ? 1 : 0.6 }}>
            {(c.supplier || c.productNumber) && (
              <div className="tiny muted">{[c.supplier, c.productNumber && `varenr. ${c.productNumber}`].filter(Boolean).join(' · ')}</div>
            )}
            {c.usedFor && <div className="small">{c.usedFor}</div>}
            {c.hazards && <div className="small" style={{ color: '#B45309' }}>⚠ {c.hazards}</div>}
            {c.location && <div className="tiny muted">Står: {c.location}</div>}
            <div className="btn-group">
              {c.hasSds ? (
                <button className="btn btn--sm" onClick={() => void downloadFile(`/api/intranet/chemicals/${c.id}/files/sds`, c.sdsFileName ?? 'sikkerhedsdatablad.pdf')}>
                  📄 Sikkerhedsdatablad
                </button>
              ) : (
                <span className="tiny muted">Intet sikkerhedsdatablad</span>
              )}
              {c.hasApb ? (
                <button className="btn btn--sm" onClick={() => void downloadFile(`/api/intranet/chemicals/${c.id}/files/apb`, c.apbFileName ?? 'brugsanvisning.pdf')}>
                  📄 Brugsanvisning
                </button>
              ) : (
                <span className="tiny muted">Ingen brugsanvisning</span>
              )}
              {isIntranetAdmin && <button className="btn btn--sm" onClick={() => setEditing(c)}>Ret</button>}
            </div>
            {c.sdsDate && (
              <div className="tiny" style={{ color: c.sdsIsOld ? '#DC2626' : undefined }}>
                Databladet er fra {formatDate(c.sdsDate)}{c.sdsIsOld ? ' - mere end 3 år gammelt, hent et nyt hos leverandøren' : ''}
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>
  )
}

function ChemicalForm({ chemical, onClose }: { chemical: Chemical | null; onClose: () => void }) {
  const save = useSaveChemical()
  const remove = useDeleteChemical()
  const upload = useUploadChemicalFile()
  const [form, setForm] = useState<ChemicalSaveInput>(() => ({
    name: chemical?.name ?? '',
    supplier: chemical?.supplier ?? '',
    productNumber: chemical?.productNumber ?? '',
    usedFor: chemical?.usedFor ?? '',
    location: chemical?.location ?? '',
    hazards: chemical?.hazards ?? '',
    sdsDate: chemical?.sdsDate ?? null,
    isActive: chemical?.isActive ?? true
  }))
  const [sds, setSds] = useState<File | null>(null)
  const [apb, setApb] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const set = <K extends keyof ChemicalSaveInput>(key: K, value: ChemicalSaveInput[K]) => setForm((f) => ({ ...f, [key]: value }))
  const busy = save.isPending || upload.isPending

  async function submit() {
    setError(null)
    try {
      const saved = await save.mutateAsync({ id: chemical?.id, input: form })
      if (sds) await upload.mutateAsync({ id: saved.id, kind: 'sds', file: sds })
      if (apb) await upload.mutateAsync({ id: saved.id, kind: 'apb', file: apb })
      onClose()
    } catch (err) {
      setError(errorText(err, 'Produktet kunne ikke gemmes.'))
    }
  }

  const field = (key: 'name' | 'supplier' | 'productNumber' | 'usedFor' | 'location' | 'hazards', label: string, placeholder = '') => (
    <label className="field">
      <span className="field__label">{label}</span>
      <input value={form[key] ?? ''} placeholder={placeholder} onChange={(e) => set(key, e.target.value)} />
    </label>
  )

  return (
    <Card title={chemical ? `Ret ${chemical.name}` : 'Tilføj produkt'}>
      <div className="stack" style={{ gap: 10 }}>
        <div className="form-grid">
          {field('name', 'Produkt *', 'Fx Brandskum B1')}
          {field('supplier', 'Leverandør')}
          {field('productNumber', 'Varenummer')}
          {field('location', 'Hvor står det', 'Fx bilerne og lageret')}
        </div>
        {field('usedFor', 'Bruges til', 'Fx lukning af gennemføringer')}
        {field('hazards', 'Fare og værnemidler', 'Fx brandfarlig - handsker og briller')}
        <div className="form-grid">
          <label className="field">
            <span className="field__label">Sikkerhedsdatablad (PDF){chemical?.hasSds ? ' - erstatter det nuværende' : ''}</span>
            <input type="file" accept=".pdf,image/*" onChange={(e) => setSds(e.target.files?.[0] ?? null)} />
          </label>
          <label className="field">
            <span className="field__label">Databladets dato</span>
            <input type="date" value={form.sdsDate ?? ''} onChange={(e) => set('sdsDate', e.target.value || null)} />
          </label>
          <label className="field">
            <span className="field__label">Arbejdspladsbrugsanvisning (PDF){chemical?.hasApb ? ' - erstatter den nuværende' : ''}</span>
            <input type="file" accept=".pdf,image/*" onChange={(e) => setApb(e.target.files?.[0] ?? null)} />
          </label>
        </div>
        <label className="row small" style={{ gap: 8 }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />
          Vi bruger det stadig
        </label>
        <div className="tiny muted">Filerne lægges i SharePoint under Intranet/Kemikalier. Kontoret får besked, når et datablad bliver 3 år gammelt.</div>
        {error && <div className="error-box">{error}</div>}
        <div className="btn-group">
          <button className="btn btn--primary" disabled={!form.name.trim() || busy} onClick={() => void submit()}>
            {busy ? 'Gemmer ...' : 'Gem'}
          </button>
          <button className="btn" onClick={onClose}>Annullér</button>
          {chemical && (
            <button
              className="btn"
              disabled={remove.isPending}
              onClick={() => {
                if (!window.confirm(`Fjern ${chemical.name} fra listen? Filerne bliver liggende i SharePoint.`)) return
                remove.mutate(chemical.id, { onSuccess: onClose, onError: (err) => setError(errorText(err, 'Kunne ikke fjernes.')) })
              }}
            >
              Fjern
            </button>
          )}
        </div>
      </div>
    </Card>
  )
}
